/**
 * TTPChatWidget - Pre-built UI widget for text chat using TextChatSDK
 * Similar to AgentWidget but designed for text-based chat
 */

import TextChatSDK from '../core/TextChatSDK.js';
import { VoiceInterface } from './VoiceInterface.js';
import { TextInterface } from './TextInterface.js';
import { LandingScreen } from './LandingScreen.js';
import widgetTranslations from './widget-translations.js';

export class TTPChatWidget {
  constructor(config = {}) {
    // Merge user config with defaults (backward compatible with simple config)
    this.config = this.mergeWithDefaults(config);
    this.sdk = new TextChatSDK(this.config);
    this.isOpen = false;
    this.isActive = false;
    this.translations = widgetTranslations;
    // Track current view state in unified mode to prevent resetting when widget is recreated
    this.currentView = 'landing'; // 'landing', 'text', or 'voice'
    // Race condition prevention: prevent multiple rapid clicks on Voice Call button
    this.isStartingCall = false;
    // Shadow DOM root for CSS isolation
    this.shadowRoot = null;
    // Container for widget elements (shadow root or regular DOM)
    this.container = null;
    // Pending client tools queue (for tools registered before SDK is ready)
    this._pendingClientTools = null;
    
    // Create shadow host and root first (before interfaces, so we can pass shadowRoot to them)
    if (this.config.useShadowDOM) {
      this.createShadowHost();
      this.container = this.shadowRoot;
    } else {
      // No Shadow DOM - create regular container and append to body
      this.createRegularContainer();
      this.container = this.regularContainer;
      this.shadowRoot = document; // For querySelector/getElementById compatibility
    }
    
    // Initialize interfaces with proper config (after shadow root is created)
    // Voice interface needs voice config merged with main config
    const voiceConfig = {
      ...this.config,
      ...this.config.voice,
      // Pass through text input placeholder for voice text input
      inputPlaceholder: this.config.text?.inputPlaceholder || this.config.inputPlaceholder || 'Type your message...',
      // Pass through send button color for voice send button
      sendButtonColor: this.config.text?.sendButtonColor || this.config.sendButtonColor || '#7C3AED',
      language: this.config.voice?.language || this.config.language || 'en',
      signedUrl: this.config.signedUrl, // Pass through signedUrl if provided
      agentSettingsOverride: this.config.agentSettingsOverride, // Pass through agentSettingsOverride if provided
      visualAssistant: this.config.visualAssistant, // Pass through visualAssistant config
      translations: this.translations,
      shadowRoot: this.shadowRoot, // Pass shadow root reference
      textSDK: this.sdk, // Pass TextChatSDK reference for sending text messages during voice calls
      // Output format defaults for widget: 44100 Hz, 16-bit, PCM, raw
      outputContainer: this.config.voice?.outputContainer || 'raw',
      outputEncoding: this.config.voice?.outputEncoding || 'pcm',
      outputSampleRate: this.config.voice?.outputSampleRate || 44100,
      outputChannels: this.config.voice?.outputChannels || 1,
      outputBitDepth: this.config.voice?.outputBitDepth || 16,
      // Pass event callbacks to voice interface
      onConversationStart: this.config.onConversationStart,
      onConversationEnd: this.config.onConversationEnd,
      onBargeIn: this.config.onBargeIn,
      onAudioStartPlaying: this.config.onAudioStartPlaying,
      onAudioStoppedPlaying: this.config.onAudioStoppedPlaying,
      onSubtitleDisplay: this.config.onSubtitleDisplay,
      onVoiceCallButtonClick: this.config.onVoiceCallButtonClick,
      // Callback when call ends - show landing screen (only in unified mode)
      // BUT: Don't return to landing if we're showing a domain error
      onCallEnd: () => {
        // Check if we're showing a domain error - if so, stay on voice interface
        const idleState = this.voiceInterface?.shadowRoot?.getElementById('voiceIdleState');
        if (idleState && idleState.style.display !== 'none') {
          const statusTitle = idleState.querySelector('.voice-status-title');
          if (statusTitle) {
            const titleText = statusTitle.textContent.trim();
            const domainErrorTitle = this.voiceInterface?.t('domainNotValidated');
            if (titleText === domainErrorTitle || titleText.includes('Domain not') || titleText.includes('not whitelisted')) {
              console.log('🚫 Domain error is displayed - NOT returning to landing screen from onCallEnd');
              return; // Stay on voice interface showing error
            }
          }
        }
        
        const widgetMode = this.config.behavior?.mode || 'unified';
        if (widgetMode === 'unified') {
          this.showLanding();
        }
      },
      // Callback to ensure voice interface is visible (used for domain errors)
      onShowVoice: () => {
        this.showVoice();
      },
      // Callback to show landing page (used by domain error back button)
      onShowLanding: () => {
        this.showLanding();
      },
      // Callback to show error toast (for domain errors, etc.)
      onErrorToast: (message, type) => {
        this.showErrorToast(message, type);
      }
    };
    this.voiceInterface = new VoiceInterface(voiceConfig);
    // Text interface needs text config merged with main config
    const textConfig = {
      ...this.config,
      ...this.config.text,
      translations: this.translations,
      shadowRoot: this.shadowRoot // Pass shadow root reference
    };
    this.textInterface = new TextInterface(textConfig, this.sdk);
    
    // Landing screen (only for unified mode)
    const widgetMode = this.config.behavior?.mode || 'unified';
    if (widgetMode === 'unified') {
      const landingConfig = {
        ...this.config,
        translations: this.translations,
        shadowRoot: this.shadowRoot
      };
      this.landingScreen = new LandingScreen(landingConfig);
    } else {
      this.landingScreen = null;
    }
    
    // Now create widget HTML and populate shadow root
    this.createWidget();
    
    this.setupEventHandlers();
    
    // Start open if configured (immediately)
    if (this.config.behavior.startOpen || this.config.behavior.autoOpen) {
      const panel = this.shadowRoot?.getElementById('text-chat-panel');
      if (panel) {
        this.isOpen = true;
        panel.classList.add('open');
      }
    }
    
    // Check for resume state and auto-resume if needed
    this.checkAndHandleResume();
  }
  
  /**
   * Check for resume state and auto-resume call if needed
   */
  checkAndHandleResume() {
    const resumeInfo = this.voiceInterface.checkAndHandleResume();
    
    if (resumeInfo) {
      console.log('🔄 [WIDGET] Auto-resuming call from page navigation');
      console.log('   From:', resumeInfo.fromPage, '→ To:', resumeInfo.toPage);
      
      // Auto-open widget
      this.open();
      
      // Switch to voice mode UI (skip landing screen, go directly to voice interface)
      this.showVoice();
      
      // Set currentView to 'voice' to prevent showing landing screen
      this.currentView = 'voice';
      
      // Auto-start voice call with resume
      // The conversationId is already stored in sessionStorage and will be sent via query param
      // Use a small delay to ensure widget is fully opened
      setTimeout(() => {
        this.startVoiceCall().then(() => {
          console.log('✅ [WIDGET] Auto-resumed call successfully');
        }).catch((error) => {
          console.error('❌ [WIDGET] Failed to auto-resume call:', error);
          // On error, could show landing screen
          const widgetMode = this.config.behavior?.mode || 'unified';
          if (widgetMode === 'unified') {
            this.showLanding();
          }
        });
      }, 100);
    }
  }
  
  /**
   * Open the widget
   */
  open() {
    if (this.isOpen) return;
    
    const panel = this.shadowRoot?.getElementById('text-chat-panel');
    if (panel) {
      this.isOpen = true;
      panel.classList.add('open');
    }
  }

  /**
   * Merge user configuration with defaults
   * Supports both simple config (backward compatible) and advanced config
   */
  mergeWithDefaults(userConfig) {
    // Handle legacy position string format
    let positionConfig = userConfig.position || 'bottom-right';
    if (typeof positionConfig === 'string') {
      // Convert legacy format 'bottom-right' to new format
      const parts = positionConfig.split('-');
      positionConfig = {
        vertical: parts[0] || 'bottom',
        horizontal: parts[1] || 'right',
        offset: userConfig.positionOffset || { x: 20, y: 20 }
      };
    }

    // Handle legacy primaryColor - default to white (for floating button)
    const primaryColor = userConfig.primaryColor || userConfig.button?.primaryColor || userConfig.button?.backgroundColor || '#FFFFFF';
    
    // FIX: Don't calculate headerColor here - it causes buttons to inherit header color
    // Instead, use explicit defaults for landing screen colors that don't depend on header

    return {
      // Required (agentId is required, appId is optional)
      agentId: userConfig.agentId,
      appId: userConfig.appId,
      signedUrl: userConfig.signedUrl, // Optional - signed URL for secure authentication (for voice)
      agentSettingsOverride: userConfig.agentSettingsOverride, // Optional - agent settings override (requires signed URL with allowOverride=true)
      demo: userConfig.demo !== false, // Optional - defaults to true
      direction: userConfig.direction || 'ltr', // Optional - text direction: 'ltr' or 'rtl'
      language: userConfig.language || 'en', // Optional - language for voice (defaults to 'en')
      
      // Icon/Image Configuration
      icon: {
        type: userConfig.icon?.type || 'custom', // 'microphone', 'custom', 'emoji', 'text'
        customImage: userConfig.icon?.customImage || 'https://talktopc.com/logo192.png',
        emoji: userConfig.icon?.emoji || '🎤',
        text: userConfig.icon?.text || 'AI',
        size: userConfig.icon?.size || 'medium', // 'small', 'medium', 'large', 'xl'
        backgroundColor: userConfig.icon?.backgroundColor || '#FFFFFF',
        ...userConfig.icon
      },
      
      // Positioning Configuration (supports both object and legacy string)
      position: {
        vertical: positionConfig.vertical || 'bottom',
        horizontal: positionConfig.horizontal || 'right',
        offset: positionConfig.offset || { x: 20, y: 20 },
        ...(typeof userConfig.position === 'object' ? userConfig.position : {})
      },
      
      // Button Configuration
      button: {
        size: userConfig.button?.size || 'medium',
        shape: userConfig.button?.shape || 'circle',
        // Floating button colors (main button) - default to white
        backgroundColor: userConfig.button?.backgroundColor || userConfig.icon?.backgroundColor || '#FFFFFF',
        hoverColor: userConfig.button?.hoverColor || '#D3D3D3', // Light gray default
        shadow: userConfig.button?.shadow !== false,
        shadowColor: userConfig.button?.shadowColor || 'rgba(0,0,0,0.15)',
        // Legacy support - map to backgroundColor
        primaryColor: userConfig.button?.backgroundColor || userConfig.icon?.backgroundColor || '#FFFFFF',
        ...userConfig.button
      },
      
      // Panel Configuration (common to both voice and text)
      panel: {
        width: userConfig.panel?.width || 360,
        height: userConfig.panel?.height || 500,
        borderRadius: userConfig.panel?.borderRadius || 24,
        backgroundColor: userConfig.panel?.backgroundColor || '#FFFFFF',
        backdropFilter: userConfig.panel?.backdropFilter || null,
        border: userConfig.panel?.border || '1px solid #E5E7EB', // Default gray border, fully customizable
        ...userConfig.panel
      },
      
      // Voice-specific Configuration
      voice: {
        // Voice button colors (inside panel)
        micButtonColor: userConfig.voice?.micButtonColor || userConfig.panel?.micButtonColor || primaryColor,
        micButtonActiveColor: userConfig.voice?.micButtonActiveColor || userConfig.panel?.micButtonActiveColor || '#EF4444',
        // Voice button hint text (below button)
        micButtonHint: {
          text: userConfig.voice?.micButtonHint?.text || userConfig.panel?.micButtonHint?.text || 'Click the button to start voice conversation',
          color: userConfig.voice?.micButtonHint?.color || userConfig.panel?.micButtonHint?.color || '#6B7280',
          fontSize: userConfig.voice?.micButtonHint?.fontSize || userConfig.panel?.micButtonHint?.fontSize || '12px',
          ...userConfig.voice?.micButtonHint,
          ...userConfig.panel?.micButtonHint
        },
        // Voice interface colors
        avatarBackgroundColor: userConfig.voice?.avatarBackgroundColor || '#667eea',
        avatarActiveBackgroundColor: userConfig.voice?.avatarActiveBackgroundColor || '#667eea',
        statusTitleColor: userConfig.voice?.statusTitleColor || '#1e293b',
        statusSubtitleColor: userConfig.voice?.statusSubtitleColor || '#64748b',
        statusDotColor: userConfig.voice?.statusDotColor || '#10b981',
        statusText: userConfig.voice?.statusText || null,
        startCallButtonColor: userConfig.voice?.startCallButtonColor || '#667eea',
        startCallButtonTextColor: userConfig.voice?.startCallButtonTextColor || '#FFFFFF',
        startCallTitle: userConfig.voice?.startCallTitle || null,
        startCallSubtitle: userConfig.voice?.startCallSubtitle || null,
        startCallButtonText: userConfig.voice?.startCallButtonText || null,
        transcriptBackgroundColor: userConfig.voice?.transcriptBackgroundColor || '#FFFFFF',
        transcriptTextColor: userConfig.voice?.transcriptTextColor || '#1e293b',
        transcriptLabelColor: userConfig.voice?.transcriptLabelColor || '#94a3b8',
        controlButtonColor: userConfig.voice?.controlButtonColor || '#FFFFFF',
        controlButtonSecondaryColor: userConfig.voice?.controlButtonSecondaryColor || '#64748b',
        endCallButtonColor: userConfig.voice?.endCallButtonColor || '#ef4444',
        // Voice language setting
        language: userConfig.voice?.language || userConfig.language || 'en',
        ...userConfig.voice
      },
      
      // Text-specific Configuration
      text: {
        // Send button colors (inside panel)
        sendButtonColor: userConfig.text?.sendButtonColor || userConfig.panel?.sendButtonColor || '#7C3AED', // Purple default
        sendButtonHoverColor: userConfig.text?.sendButtonHoverColor || userConfig.panel?.sendButtonHoverColor || '#6D28D9',
        sendButtonActiveColor: userConfig.text?.sendButtonActiveColor || userConfig.panel?.sendButtonActiveColor || '#6D28D9', // Purple darker for active
        sendButtonText: userConfig.text?.sendButtonText || userConfig.panel?.sendButtonText || '➤',
        sendButtonTextColor: userConfig.text?.sendButtonTextColor || userConfig.panel?.sendButtonTextColor || '#FFFFFF',
        sendButtonFontSize: userConfig.text?.sendButtonFontSize || userConfig.panel?.sendButtonFontSize || '18px',
        sendButtonFontWeight: userConfig.text?.sendButtonFontWeight || userConfig.panel?.sendButtonFontWeight || '500',
        // Send button hint text (below button or near it)
        sendButtonHint: {
          text: userConfig.text?.sendButtonHint?.text || userConfig.panel?.sendButtonHint?.text || '',
          color: userConfig.text?.sendButtonHint?.color || userConfig.panel?.sendButtonHint?.color || '#6B7280',
          fontSize: userConfig.text?.sendButtonHint?.fontSize || userConfig.panel?.sendButtonHint?.fontSize || '12px',
          ...userConfig.text?.sendButtonHint,
          ...userConfig.panel?.sendButtonHint
        },
        // Input field configuration
        inputPlaceholder: userConfig.text?.inputPlaceholder || userConfig.panel?.inputPlaceholder || 'Type your message...',
        inputBorderColor: userConfig.text?.inputBorderColor || userConfig.panel?.inputBorderColor || '#E5E7EB',
        inputFocusColor: userConfig.text?.inputFocusColor || userConfig.panel?.inputFocusColor || '#7C3AED', // Purple default
        inputBackgroundColor: userConfig.text?.inputBackgroundColor || userConfig.panel?.inputBackgroundColor || '#FFFFFF',
        inputTextColor: userConfig.text?.inputTextColor || userConfig.panel?.inputTextColor || '#1F2937',
        inputFontSize: userConfig.text?.inputFontSize || userConfig.panel?.inputFontSize || '14px',
        inputBorderRadius: userConfig.text?.inputBorderRadius || userConfig.panel?.inputBorderRadius || 20,
        inputPadding: userConfig.text?.inputPadding || userConfig.panel?.inputPadding || '6px 14px',
        ...userConfig.text
      },
      
      // Landing Screen Configuration (only for unified mode)
      // FIX: Use explicit defaults instead of headerColor to prevent buttons from inheriting header color
      landing: (() => {
        // Filter out empty strings from userConfig.landing to prevent overriding defaults
        const cleanLandingConfig = {};
        if (userConfig.landing) {
          Object.keys(userConfig.landing).forEach(key => {
            const value = userConfig.landing[key];
            // Include non-empty values, but also include boolean false values
            // Skip only: null, undefined, and empty strings
            if (value !== null && value !== undefined && value !== '') {
              cleanLandingConfig[key] = value;
            } else if (value === false) {
              // Explicitly include false boolean values (e.g., logoBackgroundEnabled: false)
              cleanLandingConfig[key] = value;
            }
          });
        }
        
        return {
        backgroundColor: cleanLandingConfig.backgroundColor || 'linear-gradient(180deg, #f8fafc 0%, #e0e7ff 100%)',
        logo: cleanLandingConfig.logo || '🤖',
        title: cleanLandingConfig.title || null, // null means use default translated text
        titleColor: cleanLandingConfig.titleColor || '#1e293b',
        modeCardBackgroundColor: cleanLandingConfig.modeCardBackgroundColor || '#FFFFFF',
        modeCardBorderColor: cleanLandingConfig.modeCardBorderColor || '#E2E8F0',
        modeCardHoverBorderColor: cleanLandingConfig.modeCardHoverBorderColor || '#7C3AED', // Fixed: Use explicit default instead of headerColor
        modeCardIconBackgroundColor: cleanLandingConfig.modeCardIconBackgroundColor || '#7C3AED', // Fixed: Use explicit default instead of headerColor
        modeCardTitleColor: cleanLandingConfig.modeCardTitleColor || '#111827',
        voiceCardIcon: cleanLandingConfig.voiceCardIcon || '🎤',
        textCardIcon: cleanLandingConfig.textCardIcon || '💬',
        voiceCardTitle: cleanLandingConfig.voiceCardTitle || null,
        textCardTitle: cleanLandingConfig.textCardTitle || null,
        ...cleanLandingConfig // Spread cleaned config (no empty strings)
        };
      })(),
      
      // Header Configuration (top of panel)
      header: (() => {
        // Filter out empty strings to prevent overriding defaults
        const cleanHeaderConfig = {};
        if (userConfig.header) {
          Object.keys(userConfig.header).forEach(key => {
            const value = userConfig.header[key];
            // For online indicator properties, allow empty strings (they might be intentionally set)
            // For other properties, only include non-empty values
            if (key.startsWith('onlineIndicator')) {
              if (value !== null && value !== undefined) {
                cleanHeaderConfig[key] = value;
              }
            } else {
              // Only include non-empty values (skip empty strings, null, undefined)
              if (value !== null && value !== undefined && value !== '') {
                cleanHeaderConfig[key] = value;
              }
            }
          });
        }
        
        const finalHeader = {
          title: cleanHeaderConfig.title || 'Chat Assistant',
          showTitle: cleanHeaderConfig.showTitle !== false,
          backgroundColor: cleanHeaderConfig.backgroundColor || '#7C3AED', // Default purple
          textColor: cleanHeaderConfig.textColor || '#FFFFFF',
          showCloseButton: cleanHeaderConfig.showCloseButton !== false,
          ...cleanHeaderConfig // Spread cleaned config (includes online indicator properties)
        };
        
        
        return finalHeader;
      })(),
      
      // Footer Configuration (TTP Branding - always shown by default)
      footer: {
        show: userConfig.footer?.show !== false, // Default true
        backgroundColor: userConfig.footer?.backgroundColor || '#f9fafb',
        textColor: userConfig.footer?.textColor || '#9ca3af',
        hoverColor: userConfig.footer?.hoverColor || '#7C3AED',
        ...userConfig.footer
      },
      
      // Tooltips Configuration
      tooltips: {
        newChat: userConfig.tooltips?.newChat || null, // null means use default based on direction
        back: userConfig.tooltips?.back || null,
        close: userConfig.tooltips?.close || null,
        mute: userConfig.tooltips?.mute || null,
        speaker: userConfig.tooltips?.speaker || null,
        endCall: userConfig.tooltips?.endCall || null,
        ...userConfig.tooltips
      },
      
      // Messages Configuration
      messages: {
        userBackgroundColor: userConfig.messages?.userBackgroundColor || '#E5E7EB',
        agentBackgroundColor: userConfig.messages?.agentBackgroundColor || '#F3F4F6',
        systemBackgroundColor: userConfig.messages?.systemBackgroundColor || '#DCFCE7',
        errorBackgroundColor: userConfig.messages?.errorBackgroundColor || '#FEE2E2',
        textColor: userConfig.messages?.textColor || '#1F2937', // Fallback for backward compatibility
        userTextColor: userConfig.messages?.userTextColor || userConfig.messages?.textColor || '#1F2937',
        agentTextColor: userConfig.messages?.agentTextColor || userConfig.messages?.textColor || '#1F2937',
        userAvatarIcon: userConfig.messages?.userAvatarIcon || '👤',
        agentAvatarIcon: userConfig.messages?.agentAvatarIcon || '🤖',
        fontSize: userConfig.messages?.fontSize || '14px',
        borderRadius: userConfig.messages?.borderRadius || 16,
        ...userConfig.messages
      },
      
      // Animation Configuration
      animation: {
        enableHover: userConfig.animation?.enableHover !== false,
        enablePulse: userConfig.animation?.enablePulse !== false,
        enableSlide: userConfig.animation?.enableSlide !== false,
        duration: userConfig.animation?.duration || 0.3,
        ...userConfig.animation
      },
      
      // Prompt Animation Configuration
      promptAnimation: {
        enabled: userConfig.promptAnimation?.enabled === true, // Default false
        text: userConfig.promptAnimation?.text || 'Try me!',
        backgroundColor: userConfig.promptAnimation?.backgroundColor || 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        textColor: userConfig.promptAnimation?.textColor || '#ffffff',
        animationType: userConfig.promptAnimation?.animationType || 'bounce', // 'bounce' | 'pulse' | 'float' | 'none'
        showShimmer: userConfig.promptAnimation?.showShimmer !== false, // Default true
        showPulseRings: userConfig.promptAnimation?.showPulseRings !== false, // Default true
        hideAfterClick: userConfig.promptAnimation?.hideAfterClick !== false, // Default true
        hideAfterSeconds: userConfig.promptAnimation?.hideAfterSeconds !== undefined ? userConfig.promptAnimation.hideAfterSeconds : null, // null = never
        position: userConfig.promptAnimation?.position || 'top', // 'top' | 'left' | 'right'
        ...userConfig.promptAnimation
      },
      
      // Behavior Configuration
      behavior: {
        autoOpen: userConfig.behavior?.autoOpen || false,
        startOpen: userConfig.behavior?.startOpen || false,
        hidden: userConfig.behavior?.hidden || false,
        autoConnect: userConfig.behavior?.autoConnect || false,
        showWelcomeMessage: userConfig.behavior?.showWelcomeMessage !== false,
        welcomeMessage: userConfig.behavior?.welcomeMessage || 'Hello! How can I help you today?',
        // Voice selection landing (enabled by default)
        enableVoiceMode: userConfig.behavior?.enableVoiceMode !== undefined ? userConfig.behavior.enableVoiceMode : true,
        // Widget mode: 'unified' (both voice and text with landing screen), 'voice-only' (only voice), 'text-only' (only text)
        mode: userConfig.behavior?.mode || 'unified',
        ...userConfig.behavior
      },
      
      // Accessibility Configuration
      accessibility: {
        ariaLabel: userConfig.accessibility?.ariaLabel || 'Chat Assistant',
        ariaDescription: userConfig.accessibility?.ariaDescription || 'Click to open chat assistant',
        keyboardNavigation: userConfig.accessibility?.keyboardNavigation !== false,
        ...userConfig.accessibility
      },
      
      // Custom CSS
      customStyles: userConfig.customStyles || '',
      
      // Shadow DOM Configuration
      useShadowDOM: userConfig.useShadowDOM !== false, // Default true for backward compatibility
      
      // Variables for the agent
      variables: userConfig.variables || {},
      // TEST: allow forcing a conversationId for debugging
      forceConversationId: userConfig.forceConversationId,
      
      // Visual Assistant Configuration
      visualAssistant: userConfig.visualAssistant,
      
      // Event callbacks/hooks
      onConversationStart: userConfig.onConversationStart,
      onConversationEnd: userConfig.onConversationEnd,
      onBargeIn: userConfig.onBargeIn,
      onAudioStartPlaying: userConfig.onAudioStartPlaying,
      onAudioStoppedPlaying: userConfig.onAudioStoppedPlaying,
      onSubtitleDisplay: userConfig.onSubtitleDisplay,
      onVoiceCallButtonClick: userConfig.onVoiceCallButtonClick,

      // Legacy support (for backward compatibility)
      primaryColor: primaryColor,
      // Keep position string for backward compatibility
      ...(typeof userConfig.position === 'string' ? { positionString: userConfig.position } : {})
    };
  }

  setupEventHandlers() {
    // Handle domain validation errors
    this.sdk.on('domainError', (error) => {
      this.textInterface.showError(error);
      this.textInterface.stopStreamingState();
    });

    this.sdk.on('error', (error) => {
      // Domain errors are handled by domainError event, skip here
      if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')))) {
        return; // Already handled by domainError event
      }
      this.textInterface.showError(error.message || error);
      this.textInterface.stopStreamingState();
    });

    this.sdk.on('chunk', (chunk) => {
      this.textInterface.appendStreamingChunk(chunk);
    });

    this.sdk.on('done', ({ text }) => {
      this.textInterface.finalizeStreaming(text);
    });
  }

  /**
   * Create shadow host and attach shadow DOM
   */
  createShadowHost() {
    // Remove existing shadow host if it exists
    const existingHost = document.getElementById('ttp-widget-shadow-host');
    if (existingHost) {
      existingHost.remove();
    }
    
    // Create shadow host element
    const host = document.createElement('div');
    host.id = 'ttp-widget-shadow-host';
    // IMPORTANT: Host covers viewport but is click-through (pointer-events: none)
    // The actual button and panel have pointer-events: auto to receive clicks
    // Using width/height 100% ensures fixed-position children have proper containing block
    host.style.cssText = 'position: fixed; z-index: 10000; pointer-events: none; top: 0; left: 0; width: 100%; height: 100%; overflow: visible;';
    
    // Attach shadow DOM
    this.shadowRoot = host.attachShadow({ mode: 'open' });
    
    // Append shadow host to body
    document.body.appendChild(host);
  }

  /**
   * Create regular container (no Shadow DOM) for Shopify compatibility
   */
  createRegularContainer() {
    // Remove existing container if it exists
    const existingContainer = document.getElementById('ttp-widget-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    
    // Create regular container element
    const container = document.createElement('div');
    container.id = 'ttp-widget-container';
    // Container covers viewport but is click-through (pointer-events: none)
    // The actual button and panel have pointer-events: auto to receive clicks
    container.style.cssText = 'position: fixed; z-index: 10000; pointer-events: none; top: 0; left: 0; width: 100%; height: 100%; overflow: visible;';
    
    // Append container to body
    document.body.appendChild(container);
    this.regularContainer = container;
  }

  createWidget() {
    // Ensure container exists
    if (!this.container) {
      if (this.config.useShadowDOM) {
        this.createShadowHost();
        this.container = this.shadowRoot;
      } else {
        this.createRegularContainer();
        this.container = this.regularContainer;
        this.shadowRoot = document;
      }
    }
    
    // Generate CSS reset based on mode
    let resetCSS = '';
    if (this.config.useShadowDOM) {
      // Shadow DOM mode - use :host selector
      resetCSS = `
        :host {
          all: initial;
          display: block;
          pointer-events: none;
        }
        img {
          max-width: none !important;
        }
        #text-chat-button img {
          width: 28px !important;
          height: 28px !important;
        }
        #text-chat-button {
          pointer-events: auto;
        }
        #text-chat-panel {
          pointer-events: auto;
        }
      `;
    } else {
      // Regular DOM mode - targeted reset without breaking widget styles
      resetCSS = `
        /* Reset only problematic inherited styles, not all styles */
        #text-chat-button,
        #text-chat-panel,
        #text-chat-panel * {
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        
        /* Prevent theme CSS from affecting images */
        #text-chat-button img,
        #text-chat-panel img {
          max-width: none !important;
          width: auto;
          height: auto;
        }
        
        #text-chat-button img {
          width: 28px !important;
          height: 28px !important;
        }
        
        /* Ensure button and panel can receive clicks */
        #text-chat-button,
        #text-chat-panel {
          pointer-events: auto;
        }
        
        /* Reset any inherited text styles that might break layout */
        #text-chat-panel {
          text-align: left;
          line-height: normal;
          letter-spacing: normal;
          word-spacing: normal;
          text-transform: none;
          text-indent: 0;
          text-shadow: none;
          white-space: normal;
        }
        
        /* Ensure flexbox works properly */
        #text-chat-panel .widget-shell,
        #text-chat-panel .panel-inner,
        #text-chat-panel .widget-container,
        #text-chat-panel .landing-screen,
        #text-chat-panel .voice-interface,
        #text-chat-panel .text-interface,
        #text-chat-panel .widget-header,
        #text-chat-panel .mode-selection {
          display: flex;
        }
        
        #text-chat-panel .widget-shell,
        #text-chat-panel .panel-inner,
        #text-chat-panel .widget-container,
        #text-chat-panel .landing-screen,
        #text-chat-panel .voice-interface,
        #text-chat-panel .text-interface {
          flex-direction: column;
        }
        
        #text-chat-panel .mode-selection {
          flex-direction: row;
        }
        
        /* Ensure proper stacking */
        #text-chat-button {
          z-index: 10001 !important;
        }
        
        #text-chat-panel {
          z-index: 10000 !important;
        }
      `;
    }
    
    const widgetHTML = this.generateWidgetHTML();
    
    if (this.config.useShadowDOM) {
      // Shadow DOM mode - include reset CSS in HTML and set innerHTML
      const fullHTML = `
        <style>${resetCSS}</style>
        ${widgetHTML}
      `;
      this.shadowRoot.innerHTML = fullHTML;
    } else {
      // Regular DOM mode - inject styles into head and append HTML to container
      this.injectStylesIntoHead(resetCSS);
      this.container.innerHTML = widgetHTML;
    }
    
    this.setupWidgetEvents();
    
    // Ensure initial open state if configured
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel) {
      if (this.config.behavior.startOpen || this.config.behavior.autoOpen) {
        this.isOpen = true;
        panel.classList.add('open');
        // Hide prompt if widget starts open
        this.hidePrompt();
      } else {
        // Widget starts closed - show prompt
        this.showPrompt();
      }
    } else {
      // Panel not found yet, but widget should start closed - show prompt
      // Use setTimeout to ensure prompt HTML is generated first
      setTimeout(() => {
        this.showPrompt();
      }, 100);
    }
    
    // Also ensure prompt visibility is correct after a short delay
    // This handles cases where setupPromptAnimation was called before widget HTML was ready
    setTimeout(() => {
      if (!this.isOpen) {
        this.showPrompt();
      } else {
        this.hidePrompt();
      }
    }, 200);
  }

  /**
   * Inject styles into document head (for non-Shadow DOM mode)
   */
  injectStylesIntoHead(resetCSS) {
    // Remove existing style tag if present
    const existingStyle = document.getElementById('ttp-widget-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Calculate button and icon sizes (same logic as generateWidgetHTML)
    const buttonSizes = {
      small: 50,
      medium: 60,
      large: 70,
      xl: 80
    };
    const buttonSize = buttonSizes[this.config.button?.size] || 60;
    
    const iconSizes = {
      small: 20,
      medium: 28,
      large: 36,
      xl: 44
    };
    const iconSize = iconSizes[this.config.icon?.size] || 28;
    
    // Generate full CSS
    const positionStyles = this.generatePositionStyles();
    const widgetCSS = this.generateCSS(positionStyles, buttonSize, iconSize);
    
    // Create and inject style tag
    const style = document.createElement('style');
    style.id = 'ttp-widget-styles';
    style.textContent = resetCSS + '\n' + widgetCSS;
    
    // Add custom styles if provided
    if (this.config.customStyles) {
      style.textContent += '\n' + this.config.customStyles;
    }
    
    document.head.appendChild(style);
  }

  generateWidgetHTML() {
    const pos = this.config.position;
    const btn = this.config.button;
    const icon = this.config.icon;
    const panel = this.config.panel;
    const header = this.config.header;
    const anim = this.config.animation;
    

    // Calculate button size
    const buttonSizes = {
      small: 50,
      medium: 60,
      large: 70,
      xl: 80
    };
    const buttonSize = buttonSizes[btn.size] || 60;

    // Calculate icon size
    const iconSizes = {
      small: 20,
      medium: 28,
      large: 36,
      xl: 44
    };
    const iconSize = iconSizes[icon.size] || 28;

    // Generate position styles
    const positionStyles = this.generatePositionStyles();

    // Generate icon HTML
    const iconHTML = this.generateIconHTML(iconSize);

    const widgetMode = this.config.behavior?.mode || 'unified';
    const showLanding = widgetMode === 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    const voiceEnabled = showVoice;
    
    // Helper function to get translated text
    const t = (key) => {
      const lang = this.config.language || 'en';
      const translations = this.translations[lang] || this.translations.en;
      return translations[key] || key;
    };
    
    // Helper function to get tooltip text
    const getTooltip = (key) => {
      const tooltip = this.config.tooltips?.[key];
      if (tooltip !== null && tooltip !== undefined) return tooltip;
      // Use translations for default tooltips
      const defaults = {
        newChat: t('newChat'),
        back: t('back'),
        close: t('close'),
        mute: t('mute'),
        speaker: t('speaker'),
        endCall: t('endCall')
      };
      return defaults[key] || '';
    };

    // Only include style tag in Shadow DOM mode (styles injected into head for regular DOM)
    const styleTag = this.config.useShadowDOM ? `
      <style>
        ${this.generateCSS(positionStyles, buttonSize, iconSize)}
        ${this.config.customStyles}
      </style>
    ` : '';
    
    // Generate prompt bubble HTML if enabled
    const promptConfig = this.config.promptAnimation || {};
    // Default to disabled if not specified (enabled === true means explicitly enabled)
    const isPromptEnabled = promptConfig.enabled === true;
    const promptBubbleHTML = isPromptEnabled ? this.generatePromptBubbleHTML(promptConfig, buttonSize) : '';
    
    console.log('📝 generateWidgetHTML - promptAnimation:', {
      hasConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      willGenerateHTML: !!promptBubbleHTML
    });
    
    return `
      ${styleTag}
      
      ${this.config.behavior.hidden ? '' : `
      <div id="text-chat-button-container">
        ${promptBubbleHTML}
        ${isPromptEnabled && promptConfig.showPulseRings !== false ? this.generatePulseRingsHTML(promptConfig) : ''}
        <button id=\"text-chat-button\" 
                aria-label=\"${this.config.accessibility.ariaLabel}\"
                aria-description=\"${this.config.accessibility.ariaDescription}\">\n        ${iconHTML}\n      </button>
      </div>
      `}
      
      <div id="text-chat-panel">
        <div class="widget-shell">
          <div class="panel-inner widget-container" style="direction: ${this.config.direction};">
          <div class="widget-header" style="background: ${header.backgroundColor || '#7C3AED'} !important; color: ${header.textColor || '#FFFFFF'} !important;">
            <div>
              ${header.showTitle ? `<div class="header-title">${header.title}</div>` : ''}
              <div class="header-status" style="color: ${header.onlineIndicatorColor || 'rgba(255,255,255,0.7)'} !important;">
                <span class="status-dot" style="background: ${header.onlineIndicatorDotColor || '#10b981'} !important;"></span>
                <span>${header.onlineIndicatorText !== undefined && header.onlineIndicatorText !== null ? header.onlineIndicatorText : t('online')}</span>
              </div>
            </div>
            
            <div style="display: flex; gap: 6px; align-items: center; position: relative; z-index: 1;">
              <!-- New Chat Button (hide on landing screen, show otherwise) -->
              <button class="header-icon new-chat-btn" id="newChatBtn" title="${getTooltip('newChat')}" style="${showLanding ? 'display: none;' : ''}">
                <span style="font-size: 18px; font-weight: bold;">+</span>
              </button>
              
              <!-- Back Button (only show in unified mode) -->
              ${widgetMode === 'unified' ? `<button class="header-icon back-btn" id="backBtn" title="${getTooltip('back')}" style="display: none;">
                <span style="font-size: 16px;">‹</span>
              </button>` : ''}
              
              <!-- Close Button -->
              ${header.showCloseButton ? '<button class="header-icon close-btn" id="closeBtn" title="' + getTooltip('close') + '">'+
                '<span style="font-size: 18px; font-weight: bold;">×</span>'+
                '</button>' : ''}
            </div>
          </div>

          ${showLanding && this.landingScreen ? this.landingScreen.generateHTML() : ''}

          ${showVoice ? this.voiceInterface.generateHTML() : ''}
          ${showText ? this.textInterface.generateHTML() : ''}
          ${this.generateFooterHTML()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate footer branding HTML
   */
  generateFooterHTML() {
    const footer = this.config.footer;
    if (!footer.show) return '';
    
    const refSource = encodeURIComponent(window.location.hostname || 'widget');
    const brandUrl = `https://talktopc.com?ref=widget&source=${refSource}`;
    
    return `
      <div class="widget-footer" style="
        padding: 10px 16px;
        text-align: center;
        border-top: 1px solid rgba(0,0,0,0.06);
        font-size: 11px;
        color: #64748b;
        background: #fff;
        flex-shrink: 0;
      ">
        <div class="powered-by" style="
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
        ">
          <svg viewBox="0 0 24 24" fill="currentColor" style="width: 11px; height: 11px; color: #f59e0b;">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
          </svg>
          Powered by <a href="${brandUrl}" target="_blank" rel="noopener noreferrer" style="color: #a855f7; text-decoration: none; font-weight: 500;" onmouseover="this.style.color='#9333ea'" onmouseout="this.style.color='#a855f7'">TalkToPC</a>
        </div>
      </div>
    `;
  }

  generatePositionStyles() {
    const pos = this.config.position;
    const offset = pos.offset || { x: 20, y: 20 };
    
    let styles = '';
    
    // Vertical positioning
    if (pos.vertical === 'top') {
      styles += `top: ${offset.y}px;`;
    } else if (pos.vertical === 'bottom') {
      styles += `bottom: ${offset.y}px;`;
    } else if (pos.vertical === 'center') {
      styles += `top: 50%; transform: translateY(-50%);`;
    }
    
    // Horizontal positioning
    if (pos.horizontal === 'left') {
      styles += `left: ${offset.x}px;`;
    } else if (pos.horizontal === 'right') {
      styles += `right: ${offset.x}px;`;
    } else if (pos.horizontal === 'center') {
      styles += `left: 50%; transform: translateX(-50%);`;
    }
    
    return styles;
  }

  generateIconHTML(size) {
    const icon = this.config.icon;
    
    switch (icon.type) {
      case 'custom':
        return `<img src="${icon.customImage}" alt="Chat Assistant" style="width: ${size}px; height: ${size}px; object-fit: contain;" />`;
      
      case 'emoji':
        return `<span style="font-size: ${size}px; line-height: 1;">${icon.emoji}</span>`;
      
      case 'text':
        return `<span style="font-size: ${Math.floor(size * 0.6)}px; font-weight: bold; color: white;">${icon.text}</span>`;
      
      case 'microphone':
      default:
        return `<svg viewBox="0 0 24 24" style="width: ${size}px; height: ${size}px; fill: white;">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>`;
      
      case 'chat':
        return `<svg viewBox="0 0 24 24" style="width: ${size}px; height: ${size}px; fill: white;">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
        </svg>`;
    }
  }

  /**
   * Generate prompt bubble HTML
   */
  generatePromptBubbleHTML(promptConfig, buttonSize) {
    // Extract solid color from gradient for arrow (fallback)
    let arrowColor = '#7c3aed';
    if (promptConfig.backgroundColor && promptConfig.backgroundColor.includes('gradient')) {
      // Try to extract first color from gradient
      const match = promptConfig.backgroundColor.match(/#[0-9a-fA-F]{6}/);
      if (match) {
        arrowColor = match[0];
      }
    } else {
      arrowColor = promptConfig.backgroundColor;
    }
    
    const animationClass = promptConfig.animationType === 'none' ? '' : `animation-${promptConfig.animationType}`;
    const shimmerHTML = promptConfig.showShimmer ? '<div class="prompt-bubble-shimmer"></div>' : '';
    
    return `
      <div class="prompt-bubble ${promptConfig.position} ${animationClass}" 
           id="prompt-bubble"
           role="tooltip"
           style="--prompt-bubble-bg-color: ${arrowColor};">
        <div class="prompt-bubble-content" 
             style="background: ${promptConfig.backgroundColor}; color: ${promptConfig.textColor};">
          ${shimmerHTML}
          <span style="position: relative; z-index: 1;">${promptConfig.text}</span>
        </div>
        <div class="prompt-bubble-arrow"></div>
      </div>
    `;
  }

  /**
   * Generate pulse rings HTML
   */
  generatePulseRingsHTML(promptConfig) {
    // Extract solid color from gradient for rings (fallback)
    let ringColor = '#7c3aed';
    if (promptConfig.backgroundColor && promptConfig.backgroundColor.includes('gradient')) {
      const match = promptConfig.backgroundColor.match(/#[0-9a-fA-F]{6}/);
      if (match) {
        ringColor = match[0];
      }
    } else {
      ringColor = promptConfig.backgroundColor;
    }
    
    return `
      <div class="prompt-pulse-rings" id="prompt-pulse-rings">
        <div class="prompt-pulse-ring" style="background-color: ${ringColor}33;"></div>
        <div class="prompt-pulse-ring" style="background-color: ${ringColor}1a;"></div>
      </div>
    `;
  }

  generateCSS(positionStyles, buttonSize, iconSize) {
    const btn = this.config.button;
    const icon = this.config.icon;
    const panel = this.config.panel;
    const header = this.config.header;
    const messages = this.config.messages;
    const anim = this.config.animation;
    
    // Determine which interfaces to show
    const widgetMode = this.config.behavior?.mode || 'unified';
    const showLanding = widgetMode === 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    
    // Add !important to display rules when not using Shadow DOM (to override theme CSS)
    const important = this.config.useShadowDOM === false ? ' !important' : '';
    
    // Note: Removed headerColor variable - landing screen colors now use explicit defaults
    // to prevent buttons from inheriting header backgroundColor

    return `
      /* MOBILE FIRST - Default styles for all devices */
      #text-chat-widget {
        position: fixed !important;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        /* Mobile defaults */
        right: 10px;
        bottom: 10px;
        left: auto;
        top: auto;
      }
      
      /* Desktop positioning (only on larger screens) */
      @media (min-width: 769px) {
        #text-chat-widget {
          ${positionStyles}
          right: ${this.config.position.horizontal === 'right' ? '20px' : 'auto'};
          left: ${this.config.position.horizontal === 'left' ? '20px' : 'auto'};
          bottom: ${this.config.position.vertical === 'bottom' ? '20px' : 'auto'};
          top: ${this.config.position.vertical === 'top' ? '20px' : 'auto'};
        }
      }
      
      /* Mobile override (force mobile positioning) */
      @media (max-width: 768px) {
        #text-chat-widget {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
          transform: none !important;
        }
      }
      
      @media (max-width: 480px) {
        #text-chat-widget {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      #text-chat-button {
        position: relative;
        width: ${buttonSize}px;
        height: ${buttonSize}px;
        margin: 0;
        flex-shrink: 0;
        border-radius: ${btn.shape === 'circle' ? '50%' : btn.shape === 'square' ? '0' : '12px'};
        background: ${btn.backgroundColor || icon.backgroundColor || '#7C3AED'};
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all ${anim.duration}s ease;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        user-select: none;
        min-width: 44px;
        min-height: 44px;
        z-index: 1;
      }
      
      @media (max-width: 768px) {
        #text-chat-widget {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
          transform: none !important;
        }
        
        #text-chat-button-container {
          width: 56px !important;
          height: 56px !important;
        }
        
        #text-chat-button {
          width: 56px !important;
          height: 56px !important;
          min-width: 56px !important;
          min-height: 56px !important;
          max-width: 56px !important;
          max-height: 56px !important;
        }
        
        #text-chat-panel {
          position: fixed !important;
          left: 10px !important;
          right: 10px !important;
          bottom: 92px !important; /* 56px button + 20px gap + 16px footer */
          top: 60px !important; /* Add top spacing */
          width: auto !important;
          max-width: none !important;
          height: auto !important; /* Change from max-height to auto */
          max-height: none !important; /* Remove max-height */
          transform: none !important;
          margin: 0 !important;
        }
        
        #text-chat-panel .widget-header {
          padding: 10px 14px;
          min-height: 56px;
        }
        
        #text-chat-panel .header-title {
          font-size: 15px;
        }
        
        #text-chat-panel .header-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;
          min-height: 40px;
        }
        
        #text-chat-input {
          font-size: 16px !important; /* Prevents iOS zoom on focus */
          padding: 12px 16px !important;
          min-height: 48px !important;
        }
        
        #text-chat-send {
          min-width: 48px !important;
          min-height: 48px !important;
          width: 48px !important;
          height: 48px !important;
        }
        
        .landing-screen {
          padding: 16px;
        }
        
        .landing-logo {
          font-size: 40px;
        }
        
        .landing-title {
          font-size: 18px;
          margin-bottom: 16px;
        }
        
        .mode-selection {
          flex-direction: column;
          gap: 12px;
          align-items: center;
        }
        
        .mode-card {
          max-width: 100%;
          width: 100%;
          padding: 16px;
        }
        
        .mode-card-icon {
          width: 50px;
          height: 50px;
          font-size: 28px;
        }
        
        .mode-card-title {
          font-size: 14px;
        }
      }
      
      @media (max-width: 480px) {
        #text-chat-widget {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
        
        #text-chat-button-container {
          width: 54px !important;
          height: 54px !important;
        }
        
        #text-chat-button {
          width: 54px !important;
          height: 54px !important;
          min-width: 54px !important;
          min-height: 54px !important;
        }
        
        #text-chat-panel {
          left: 8px !important;
          right: 8px !important;
          bottom: 86px !important; /* 54px button + 20px gap + 12px footer */
          top: 50px !important; /* Add top spacing for very small screens */
          height: auto !important;
          max-height: none !important;
        }
        
        #text-chat-panel .widget-header {
          padding: 8px 12px;
          min-height: 52px;
        }
        
        #text-chat-panel .header-title {
          font-size: 14px;
        }
        
        .landing-logo {
          font-size: 36px;
        }
        
        .landing-title {
          font-size: 16px;
        }
      }
      
      ${anim.enableHover ? `
        #text-chat-button:hover {
          ${btn.hoverColor ? `background: ${btn.hoverColor};` : ''}
          transform: scale(1.05);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.5);
        }
      ` : ''}
      
      /* Prompt Animation Keyframes */
      @keyframes widget-shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
      
      @keyframes widget-ripple {
        0% { transform: scale(1); opacity: 0.6; }
        100% { transform: scale(2.5); opacity: 0; }
      }
      
      @keyframes widget-float {
        0%, 100% { transform: translateX(-50%) translateY(0); }
        50% { transform: translateX(-50%) translateY(-8px); }
      }
      
      @keyframes widget-bounce {
        0%, 100% { transform: translateX(-50%) translateY(0); }
        50% { transform: translateX(-50%) translateY(-10px); }
      }
      
      @keyframes widget-pulse-ring {
        0% { transform: scale(1); opacity: 0.4; }
        100% { transform: scale(1.8); opacity: 0; }
      }
      
      /* Prompt Bubble Container */
      #text-chat-button-container {
        position: fixed;
        ${this.config.position.vertical === 'bottom' ? `bottom: ${this.config.position.offset?.y || 20}px;` : `top: ${this.config.position.offset?.y || 20}px;`}
        ${this.config.position.horizontal === 'right' ? `right: ${this.config.position.offset?.x || 20}px;` : `left: ${this.config.position.offset?.x || 20}px;`}
        width: ${buttonSize}px;
        height: ${buttonSize}px;
        z-index: 10001;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      @media (max-width: 768px) {
        #text-chat-button-container {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      @media (max-width: 480px) {
        #text-chat-button-container {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      /* Prompt Bubble Styles */
      .prompt-bubble {
        position: absolute;
        z-index: 10002;
        pointer-events: none;
        white-space: nowrap;
      }
      
      .prompt-bubble.top {
        bottom: calc(100% + 18px);
        left: 50%;
        transform: translateX(-50%);
      }
      
      .prompt-bubble.left {
        right: calc(100% + 12px);
        top: 50%;
        transform: translateY(-50%);
      }
      
      .prompt-bubble.right {
        left: calc(100% + 12px);
        top: 50%;
        transform: translateY(-50%);
      }
      
      /* Ensure animations preserve horizontal centering for top position */
      .prompt-bubble.top.animation-bounce {
        animation: widget-bounce 1s ease-in-out infinite;
        transform: translateX(-50%); /* Keep centering */
      }
      
      .prompt-bubble.top.animation-float {
        animation: widget-float 2s ease-in-out infinite;
        transform: translateX(-50%); /* Keep centering */
      }
      
      .prompt-bubble.top.animation-pulse {
        animation: pulse 2s ease-in-out infinite;
        transform: translateX(-50%); /* Keep centering */
      }
      
      .prompt-bubble-content {
        position: relative;
        padding: 8px 16px;
        border-radius: 20px;
        font-weight: 500;
        font-size: 14px;
        box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
        overflow: hidden;
      }
      
      .prompt-bubble-shimmer {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
        animation: widget-shimmer 2s infinite;
      }
      
      .prompt-bubble.animation-bounce {
        animation: widget-bounce 1s ease-in-out infinite;
      }
      
      .prompt-bubble.animation-pulse {
        animation: pulse 2s ease-in-out infinite;
      }
      
      .prompt-bubble.animation-float {
        animation: widget-float 2s ease-in-out infinite;
      }
      
      /* Ensure top-positioned bubbles maintain horizontal centering during animations */
      .prompt-bubble.top.animation-bounce,
      .prompt-bubble.top.animation-float,
      .prompt-bubble.top.animation-pulse {
        /* Animation keyframes already include translateX(-50%) for centering */
      }
      
      /* Prompt Bubble Arrow */
      .prompt-bubble-arrow {
        position: absolute;
        width: 0;
        height: 0;
        border: 8px solid transparent;
      }
      
      .prompt-bubble.top .prompt-bubble-arrow {
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-top-color: var(--prompt-bubble-bg-color, #7c3aed);
        border-bottom: none;
        margin-left: 0;
      }
      
      .prompt-bubble.left .prompt-bubble-arrow {
        left: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-left-color: var(--prompt-bubble-bg-color, #7c3aed);
        border-right: none;
      }
      
      .prompt-bubble.right .prompt-bubble-arrow {
        right: 100%;
        top: 50%;
        transform: translateY(-50%);
        border-right-color: var(--prompt-bubble-bg-color, #7c3aed);
        border-left: none;
      }
      
      /* Pulse Rings */
      .prompt-pulse-rings {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        pointer-events: none;
      }
      
      .prompt-pulse-ring {
        position: absolute;
        inset: 0;
        border-radius: 50%;
        animation: widget-pulse-ring 2s ease-out infinite;
      }
      
      .prompt-pulse-ring:nth-child(2) {
        animation-delay: 0.5s;
      }
      
      /* Mobile adjustments for prompt bubble */
      @media (max-width: 768px) {
        .prompt-bubble-content {
          font-size: 12px;
          padding: 6px 12px;
        }
        
        .prompt-bubble.top {
          bottom: calc(100% + 14px);
        }
        
        .prompt-bubble.left,
        .prompt-bubble.right {
          display: none; /* Hide side prompts on mobile */
        }
      }
      
      #text-chat-panel {
        display: none${important};
        position: fixed;
        bottom: calc(${buttonSize}px + 20px + 20px); /* Button + gap + reduced footer offset */
        ${this.config.position.horizontal === 'right' ? 'right: 20px;' : 'left: 20px;'}
        width: ${panel.width}px;
        max-width: calc(100vw - 40px);
        height: ${panel.height}px;
        max-height: calc(100vh - ${buttonSize}px - 40px - 20px); /* Account for footer height */
        background: transparent;
        border-radius: ${panel.borderRadius}px;
        border: none;
        flex-direction: column;
        overflow: hidden;
        ${panel.backdropFilter ? `backdrop-filter: ${panel.backdropFilter};` : ''}
        ${anim.enableSlide ? `transition: all ${anim.duration}s ease;` : ''}
        box-sizing: border-box;
      }
      
      #text-chat-panel.open {
        display: flex${important};
        ${anim.enableSlide ? 'transform: translateY(0); opacity: 1;' : ''}
      }

      /* Shell for gradient border/background */
      .widget-shell { width: 100%; height: 100%; padding: 0; border-radius: ${panel.borderRadius}px; background: transparent; box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden; display: flex; flex-direction: column; box-sizing: border-box; }
      .panel-inner { width: 100%; height: 100%; background: #ffffff; border-radius: ${panel.borderRadius}px; border: ${panel.border}; overflow: hidden; display:flex; flex-direction: column; padding: 0; box-sizing: border-box; max-width: 100%; }

      /* New structure styles matching provided design */
      #text-chat-panel .widget-container {
        width: 100%; height: 100%; background: #FFFFFF; overflow: visible; display: flex; flex-direction: column; border-radius: ${panel.borderRadius}px;
        container-type: size;
      }
      
      /* Ensure content areas can scroll when height is constrained */
      #text-chat-panel .widget-container > .landing-screen,
      #text-chat-panel .widget-container > .voice-interface,
      #text-chat-panel .widget-container > .text-interface {
        flex: 1;
        overflow-y: auto;
        overflow-x: visible; /* Change from hidden to visible */
      }
      
      /* Header should not scroll */
      #text-chat-panel .widget-header {
        padding: 14px 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top-left-radius: ${panel.borderRadius}px;
        border-top-right-radius: ${panel.borderRadius}px;
        flex-shrink: 0;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
        background: ${header.backgroundColor || '#7C3AED'}${important};
        color: ${header.textColor || '#FFFFFF'}${important};
      }
      
      #text-chat-panel .widget-header::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -20%;
        width: 180px;
        height: 180px;
        background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
        pointer-events: none;
      }
      
      #text-chat-panel .widget-header > div:first-child {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        position: relative;
        z-index: 1;
      }
      
      #text-chat-panel .header-title { font-size: 15px; font-weight: 600; margin: 0; }
      #text-chat-panel .header-status { display: flex; align-items: center; gap: 5px; font-size: 12px; margin: 0; }
      #text-chat-panel .status-dot { width: 6px; height: 6px; border-radius: 50%; animation: pulse 2s ease-in-out infinite; }
      /* Online indicator customization - inline styles take precedence */
      @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.15); opacity: 0.8; } }
      /* Header icon buttons */
      .header-icon {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: white;
        width: 34px;
        height: 34px;
        min-width: 34px;
        min-height: 34px;
        border-radius: 10px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        flex-shrink: 0;
        font-size: 16px;
        padding: 0;
        box-sizing: border-box;
        position: relative;
        z-index: 1;
      }
      
      .header-icon:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      
      .header-icon svg {
        pointer-events: none;
        stroke: white;
        fill: none;
      }
      
      .back-btn.visible {
        display: flex !important;
      }

      ${showLanding && this.landingScreen ? this.landingScreen.generateCSS() : ''}

      ${showVoice ? this.voiceInterface.generateCSS() : ''}
      ${showText ? this.textInterface.generateCSS() : ''}
      
      /* Footer Branding */
      .widget-footer {
        box-sizing: border-box;
      }
      
      .footer-brand-link {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }
      
      .footer-brand-link:hover {
        opacity: 0.9;
      }
      
      @media (max-width: 768px) {
        .widget-footer {
          height: 32px;
        }
        .widget-footer span {
          font-size: 10px;
        }
        .widget-footer span:last-child {
          font-size: 8px;
        }
      }
      
      #text-chat-send-hint {
        text-align: center;
        line-height: 1.4;
      }
      
      .agent-thinking {
        font-style: italic;
        color: #6B7280;
      }
    `;
  }

  setupWidgetEvents() {
    if (!this.shadowRoot) return;
    
    // Setup prompt animation visibility and interactions
    this.setupPromptAnimation();
    
    const openBtn = this.shadowRoot.getElementById('text-chat-button');
    if (openBtn) {
      openBtn.onclick = () => {
        // Hide prompt after click if configured
        if (this.config.promptAnimation.hideAfterClick) {
          this.hidePrompt();
        }
        this.togglePanel();
      };
    }
    
    const closeBtn = this.shadowRoot.getElementById('closeBtn');
    if (closeBtn) {
      closeBtn.onclick = () => {
        // Just close the widget panel - call continues in background
        // User can reopen widget to continue the call
        this._doTogglePanel();
      };
    }
    
    // Voice selection events (based on widget mode)
    const widgetMode = this.config.behavior?.mode || 'unified';
    const showLanding = widgetMode === 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const landing = this.shadowRoot.getElementById('landingScreen');
    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    
    // Setup back button handler
    if (backBtn) {
      backBtn.onclick = () => {
        // Check if we're showing a domain error - if so, clear it and go back to landing
        if (this.voiceInterface?.isShowingDomainError) {
          console.log('🔙 Back button clicked - clearing domain error and returning to landing');
          // Clear the domain error flag
          this.voiceInterface.isShowingDomainError = false;
          // Reset the connecting state (this will restore normal UI)
          if (this.voiceInterface?.resetConnectingState) {
            this.voiceInterface.resetConnectingState();
          }
          // Now show landing (will work since error flag is cleared)
          this.showLanding();
          return;
        }
        
        if (this.voiceInterface?.isActive) {
          // If call is active, show message inside widget instead of modal
          this.showBackButtonWarning();
        } else {
          // If not active, just show landing
          this.showLanding();
        }
      };
    }
    
    if (showLanding && this.landingScreen) {
      this.landingScreen.setupEventHandlers({
        onSelectVoice: async () => {
          // Prevent multiple rapid clicks
          if (this.isStartingCall) {
            console.log('⚠️ Call already starting, ignoring duplicate click');
            return;
          }
          
          // Call the callback if provided
          if (typeof this.config.onVoiceCallButtonClick === 'function') {
            try {
              this.config.onVoiceCallButtonClick({ timestamp: Date.now(), widgetState: 'start' });
            } catch (error) {
              console.error('Error in onVoiceCallButtonClick callback:', error);
            }
          }

          this.isStartingCall = true;
          
          try {
            // Show voice interface first (needed for UI state)
            // Note: If showVoice() throws, finally block will still reset flag
            this.showVoice();
            
            // Show connecting/loading state immediately
            // This provides feedback during WebSocket connection and SDK initialization
            if (this.voiceInterface?.showConnectingState) {
              this.voiceInterface.showConnectingState();
            }
            
            // CRITICAL: On mobile, show minimized bar immediately BEFORE blocking operations
            // This ensures UI is visible while permission/connection happens
            if (this.voiceInterface?.isMobile && this.voiceInterface?.createMobileMinimizedBar) {
              // Hide floating button immediately so minimized bar is visible
              const floatingButton = this.shadowRoot?.getElementById('text-chat-button') || document.getElementById('text-chat-button');
              if (floatingButton) {
                floatingButton.style.display = 'none';
              }
              // Create minimized bar with connecting state
              this.voiceInterface.createMobileMinimizedBar();
              // Show connecting status in minimized bar
              this.voiceInterface.updateMobileStatus('connecting');
              // Ensure UI is painted before starting heavy operations
              await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            }
            
            // Start the call (this will do permission checks, connection, etc.)
            await this.startVoiceCall();
            
            // Only log success if call is actually active
            if (this.voiceInterface?.isActive) {
              console.log('✅ Voice call started successfully');
            } else {
              // This shouldn't happen if startVoiceCall() throws properly, but just in case
              console.warn('⚠️ startVoiceCall() returned but call is not active');
              
              // CRITICAL: Check if we're showing a domain error - if so, stay on voice interface
              // We check by looking at the status title text content
              const idleState = this.voiceInterface?.shadowRoot?.getElementById('voiceIdleState');
              let isDomainError = false;
              
              if (idleState && idleState.style.display !== 'none') {
                const statusTitle = idleState.querySelector('.voice-status-title');
                if (statusTitle) {
                  const titleText = statusTitle.textContent.trim();
                  const domainErrorTitle = this.voiceInterface?.t('domainNotValidated');
                  console.log('🔍 Checking for domain error - titleText:', titleText, 'domainErrorTitle:', domainErrorTitle);
                  if (titleText === domainErrorTitle || titleText.includes('Domain not') || titleText.includes('not whitelisted')) {
                    isDomainError = true;
                    console.log('🚫 Domain error detected - staying on voice interface, NOT returning to landing');
                  }
                }
              }
              
              if (isDomainError) {
                // Set currentView to voice so we don't return to landing
                this.currentView = 'voice';
                // Ensure voice interface is visible
                this.showVoice();
                // Don't reset connecting state or return to landing - stay on voice interface showing error
                console.log('✅ Staying on voice interface with domain error displayed');
                return;
              }
              
              // Not a domain error - reset connecting state and return to landing
              console.log('⚠️ Not a domain error - returning to landing screen');
              if (this.voiceInterface?.resetConnectingState) {
                this.voiceInterface.resetConnectingState();
              }
              const widgetMode = this.config.behavior?.mode || 'unified';
              if (widgetMode === 'unified') {
                this.showLanding();
              }
            }
          } catch (error) {
            // Handle server rejection gracefully (don't log as error)
            if (error.isServerRejection || error.name === 'ServerRejected') {
              // Server rejection is expected - the error handler will show domain error message
              // Reset connecting state but don't log as error
              if (this.voiceInterface?.resetConnectingState) {
                this.voiceInterface.resetConnectingState();
              }
              // Don't show toast for server rejection - domain error handler will show message
              return;
            }
            
            // Log other errors (permission denied, etc.)
            console.error('❌ Failed to start voice call:', error);
            
            // Reset connecting state on error
            if (this.voiceInterface?.resetConnectingState) {
              this.voiceInterface.resetConnectingState();
            }
            
            // Clean up mobile bar if it was created early
            if (this.voiceInterface?.isMobile) {
              const existingBar = document.getElementById('mobile-voice-call-bar-container');
              if (existingBar && this.voiceInterface?.removeMobileMinimizedBar) {
                this.voiceInterface.removeMobileMinimizedBar();
              }
              // Restore floating button if it was hidden
              const floatingButton = this.shadowRoot?.getElementById('text-chat-button') || document.getElementById('text-chat-button');
              if (floatingButton) {
                floatingButton.style.display = '';
              }
            }
            
            // Show user-friendly error message (unless it's a user cancellation)
            if (error.message !== 'Call cancelled by user') {
              this.showErrorToast(
                error.message || 'Failed to start voice call. Please try again.',
                'error'
              );
            }
            
            // Return to landing screen on error
            const widgetMode = this.config.behavior?.mode || 'unified';
            if (widgetMode === 'unified') {
              this.showLanding();
            }
          } finally {
            // ✅ Always reset flag, even if showVoice() or startVoiceCall() throws
            this.isStartingCall = false;
          }
        },
        onSelectText: () => this.showText()
      });
      
      // Restore previous view state if user had selected an interface, otherwise show landing
      if (this.currentView === 'text') {
        // User was on text interface, restore it
        if (landing) landing.classList.remove('active');
        if (textInterface) textInterface.classList.add('active');
        if (voiceInterface) voiceInterface.classList.remove('active');
        if (backBtn && widgetMode === 'unified') backBtn.classList.add('visible');
        if (newChatBtn) newChatBtn.style.display = '';
      } else if (this.currentView === 'voice') {
        // User was on voice interface, restore it
        if (landing) landing.classList.remove('active');
        if (textInterface) textInterface.classList.remove('active');
        if (voiceInterface) voiceInterface.classList.add('active');
        if (backBtn && widgetMode === 'unified') backBtn.classList.add('visible');
        if (newChatBtn) newChatBtn.style.display = 'none';
      } else {
        // Initial state or landing: show landing screen
        this.currentView = 'landing';
        if (landing) landing.classList.add('active');
        if (textInterface) textInterface.classList.remove('active');
        if (voiceInterface) voiceInterface.classList.remove('active');
        // Hide back button on landing screen (only exists in unified mode)
        if (backBtn && widgetMode === 'unified') backBtn.classList.remove('visible');
      }
    } else if (widgetMode === 'voice-only') {
      // Voice-only mode: show voice interface directly
      if (voiceInterface) voiceInterface.classList.add('active');
      if (textInterface) textInterface.classList.remove('active');
      if (landing) landing.classList.remove('active');
      // Back button doesn't exist in voice-only mode (only in unified)
    } else if (widgetMode === 'text-only') {
      // Text-only mode: show text interface directly
      if (textInterface) textInterface.classList.add('active');
      if (voiceInterface) voiceInterface.classList.remove('active');
      if (landing) landing.classList.remove('active');
      // Back button doesn't exist in text-only mode (only in unified)
    }
    
    // Setup interface event handlers
    if (showVoice) {
      console.log('⚙️ Setting up voice interface event handlers');
      this.voiceInterface.setupEventHandlers();
    }
    if (showText) {
      console.log('⚙️ Setting up text interface event handlers');
      this.textInterface.setupEventHandlers();
    }
    
    // Setup header button handlers
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    
    if (newChatBtn) {
      newChatBtn.onclick = () => this.textInterface.startNewChat();
      // Hide new chat button on landing screen initially
      if (showLanding) {
        newChatBtn.style.display = 'none';
      }
    }
    
    // Keyboard navigation
    if (this.config.accessibility.keyboardNavigation) {
      this.setupKeyboardNavigation();
    }
  }

  startNewChat() {
    this.textInterface.startNewChat();
  }

  showLanding() {
    if (!this.shadowRoot) return;
    
    // CRITICAL: Don't show landing if we're displaying a domain error
    const idleState = this.voiceInterface?.shadowRoot?.getElementById('voiceIdleState');
    if (idleState && idleState.style.display !== 'none') {
      const statusTitle = idleState.querySelector('.voice-status-title');
      if (statusTitle) {
        const titleText = statusTitle.textContent.trim();
        const domainErrorTitle = this.voiceInterface?.t('domainNotValidated');
        if (titleText === domainErrorTitle || titleText.includes('Domain not') || titleText.includes('not whitelisted')) {
          console.log('🚫 showLanding() called but domain error is displayed - preventing return to landing');
          return; // Don't show landing - stay on voice interface with error
        }
      }
    }
    
    const widgetMode = this.config.behavior.mode || 'unified';
    if (widgetMode !== 'unified') return; // Only show landing in unified mode
    this.currentView = 'landing'; // Track state
    const landing = this.shadowRoot.getElementById('landingScreen');
    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    if (landing) landing.classList.add('active');
    if (textInterface) textInterface.classList.remove('active');
    if (voiceInterface) voiceInterface.classList.remove('active');
    // Hide back button on landing screen
    if (backBtn) backBtn.classList.remove('visible');
    // Hide new chat button on landing screen
    if (newChatBtn) newChatBtn.style.display = 'none';
  }

  showText() {
    if (!this.shadowRoot) return;
    this.currentView = 'text'; // Track state
    const landing = this.shadowRoot.getElementById('landingScreen');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    if (landing) landing.classList.remove('active');
    if (voiceInterface) voiceInterface.classList.remove('active');
    this.textInterface.show();
    // Show back button when not on landing (only in unified mode)
    const widgetMode = this.config.behavior.mode || 'unified';
    if (backBtn && widgetMode === 'unified') backBtn.classList.add('visible');
    // Show new chat button when not on landing
    if (newChatBtn) newChatBtn.style.display = '';
  }

  showVoice() {
    if (!this.shadowRoot) return;
    this.currentView = 'voice'; // Track state
    const landing = this.shadowRoot.getElementById('landingScreen');
    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    if (landing) landing.classList.remove('active');
    if (textInterface) textInterface.classList.remove('active');
    if (voiceInterface) voiceInterface.classList.add('active');
    // Show back button when not on landing (only in unified mode)
    const widgetMode = this.config.behavior.mode || 'unified';
    if (backBtn && widgetMode === 'unified') backBtn.classList.add('visible');
    // Hide new chat button on voice interface (not applicable)
    if (newChatBtn) newChatBtn.style.display = 'none';
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.togglePanel();
      }
    });
  }

  /**
   * Setup prompt animation visibility and auto-hide timer
   */
  setupPromptAnimation() {
    if (!this.shadowRoot) return;
    
    const promptConfig = this.config.promptAnimation || {};
    // Default to disabled if not specified (enabled === true means explicitly enabled)
    const isPromptEnabled = promptConfig.enabled === true;
    
    console.log('🎨 setupPromptAnimation called:', {
      hasPromptConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      isOpen: this.isOpen
    });
    
    if (!isPromptEnabled) {
      // Hide prompt if disabled
      console.log('❌ Prompt disabled, hiding');
      this.hidePrompt();
      return;
    }
    
    // Show or hide prompt based on widget state
    // If widget is closed, show the prompt; if open, hide it
    if (this.isOpen) {
      this.hidePrompt();
    } else {
      this.showPrompt();
    }
    
    // Auto-hide after configured seconds (only if widget is closed)
    if (!this.isOpen && promptConfig.hideAfterSeconds !== null && promptConfig.hideAfterSeconds > 0) {
      this.promptAutoHideTimer = setTimeout(() => {
        this.hidePrompt();
      }, promptConfig.hideAfterSeconds * 1000);
    }
    
    // Hide pulse rings when voice call starts (listen to voice interface events)
    if (this.voiceInterface && this.voiceInterface.sdk) {
      // Store original onConversationStart if it exists
      const originalOnConversationStart = this.config.onConversationStart;
      
      // Wrap the callback to also hide pulse rings
      this.config.onConversationStart = (...args) => {
        // Hide pulse rings when call starts
        const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (pulseRings) pulseRings.style.display = 'none';
        
        // Call original callback if it exists
        if (originalOnConversationStart) {
          originalOnConversationStart(...args);
        }
      };
      
      // Also listen to recordingStarted event from SDK if available
      if (this.voiceInterface.sdk.voiceSDK) {
        this.voiceInterface.sdk.voiceSDK.on('recordingStarted', () => {
          const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
          if (pulseRings) pulseRings.style.display = 'none';
        });
      }
    }
  }

  /**
   * Hide the prompt bubble and pulse rings
   */
  hidePrompt() {
    if (!this.shadowRoot) return;
    
    // Clear auto-hide timer if it exists
    if (this.promptAutoHideTimer) {
      clearTimeout(this.promptAutoHideTimer);
      this.promptAutoHideTimer = null;
    }
    
    // Hide prompt bubble (check both shadow root and document for compatibility)
    let promptBubble = this.shadowRoot.getElementById('prompt-bubble');
    if (!promptBubble && this.shadowRoot !== document) {
      promptBubble = document.getElementById('prompt-bubble');
    }
    if (promptBubble) {
      promptBubble.style.display = 'none';
      promptBubble.style.visibility = 'hidden';
    }
    
    // Hide pulse rings (check both shadow root and document for compatibility)
    let pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
    if (!pulseRings && this.shadowRoot !== document) {
      pulseRings = document.getElementById('prompt-pulse-rings');
    }
    if (pulseRings) {
      pulseRings.style.display = 'none';
      pulseRings.style.visibility = 'hidden';
    }
  }

  /**
   * Show the prompt bubble and pulse rings (when widget is closed)
   */
  showPrompt() {
    if (!this.shadowRoot) return;
    
    const promptConfig = this.config.promptAnimation || {};
    // Default to enabled if not specified
    const isPromptEnabled = promptConfig.enabled !== false;
    
    console.log('👁️ showPrompt called:', {
      hasPromptConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      shadowRoot: !!this.shadowRoot
    });
    
    if (!isPromptEnabled) {
      console.log('❌ Prompt not enabled, skipping show');
      return; // Don't show if disabled
    }
    
    // Function to actually show the elements
    const doShow = () => {
      // Show prompt bubble (check both shadow root and document for compatibility)
      let promptBubble = this.shadowRoot.getElementById('prompt-bubble');
      if (!promptBubble && this.shadowRoot !== document) {
        promptBubble = document.getElementById('prompt-bubble');
      }
      if (promptBubble) {
        promptBubble.style.display = 'block';
        promptBubble.style.visibility = 'visible';
        promptBubble.style.opacity = '1';
        console.log('✅ Prompt bubble shown:', promptBubble);
      } else {
        console.warn('⚠️ Prompt bubble not found in shadowRoot or document');
      }
      
      // Show pulse rings if enabled
      if (promptConfig.showPulseRings !== false) {
        let pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (!pulseRings && this.shadowRoot !== document) {
          pulseRings = document.getElementById('prompt-pulse-rings');
        }
        if (pulseRings) {
          pulseRings.style.display = 'block';
          pulseRings.style.visibility = 'visible';
          pulseRings.style.opacity = '1';
        }
      }
    };
    
    // Try to show immediately
    doShow();
    
    // If elements don't exist yet, retry after delays (for widget initialization)
    const promptBubble = this.shadowRoot.getElementById('prompt-bubble');
    const pulseRings = promptConfig.showPulseRings !== false ? this.shadowRoot.getElementById('prompt-pulse-rings') : null;
    
    if (!promptBubble || (promptConfig.showPulseRings !== false && !pulseRings)) {
      // Retry after short delay
      setTimeout(() => {
        doShow();
      }, 100);
      
      // Retry after longer delay if still not found
      setTimeout(() => {
        doShow();
      }, 500);
    }
    
    // Restart auto-hide timer if configured
    if (promptConfig.hideAfterSeconds !== null && promptConfig.hideAfterSeconds > 0) {
      // Clear any existing timer
      if (this.promptAutoHideTimer) {
        clearTimeout(this.promptAutoHideTimer);
      }
      // Start new timer
      this.promptAutoHideTimer = setTimeout(() => {
        this.hidePrompt();
      }, promptConfig.hideAfterSeconds * 1000);
    }
  }

  togglePanel() {
    if (!this.shadowRoot) return;
    
    // Just toggle the panel - call continues regardless of open/close state
    // User can minimize/maximize widget without ending the call
    this._doTogglePanel();
  }
  
  _doTogglePanel() {
    if (!this.shadowRoot) return;
    this.isOpen = !this.isOpen;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel) {
      panel.classList.toggle('open');
    }
    
    // Hide/show prompt animation based on panel state
    if (this.isOpen) {
      // Panel is opening - hide prompt
      this.hidePrompt();
      
      setTimeout(() => {
        const input = this.shadowRoot.getElementById('messageInput');
        if (input) input.focus();
      }, 100);
    } else {
      // Panel is closing - show prompt (if enabled)
      // Call showPrompt immediately, then also retry after a delay to ensure it's visible
      this.showPrompt();
      setTimeout(() => {
        // Double-check and ensure prompt is visible
        const promptBubble = this.shadowRoot.getElementById('prompt-bubble');
        if (promptBubble && promptBubble.style.display === 'none') {
          promptBubble.style.display = 'block';
        }
        const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (pulseRings && this.config.promptAnimation?.showPulseRings !== false) {
          if (pulseRings.style.display === 'none') {
            pulseRings.style.display = 'block';
          }
        }
      }, 100);
    }
    
    // Auto-connect if enabled
    if (this.isOpen && !this.isActive && this.config.behavior.autoConnect) {
      this.startChat();
    }
  }
  
  /**
   * Show error toast/notification to user
   * @param {string} message - Error message to display
   * @param {string} type - Type of notification ('error', 'warning', 'info')
   */
  showErrorToast(message, type = 'error') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `widget-toast widget-toast-${type}`;
    toast.textContent = message;
    
    // Style the toast
    const bgColor = type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#3b82f6';
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: bgColor,
      color: 'white',
      padding: '12px 24px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: '10000',
      fontSize: '14px',
      fontWeight: '500',
      maxWidth: '90%',
      textAlign: 'center',
      animation: 'slideUp 0.3s ease-out',
      fontFamily: 'inherit'
    });
    
    // Add CSS animation if not already added
    if (!document.getElementById('widget-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'widget-toast-styles';
      style.textContent = `
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          to {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Add to shadow root or document
    const container = this.shadowRoot || document.body;
    container.appendChild(toast);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }
  
  /**
   * Show confirmation modal (reusing modal pattern)
   * @param {Object} options - Modal configuration
   */
  showConfirmModal(options) {
    const {
      title = 'Confirm',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      confirmButtonClass = 'primary',
      onConfirm,
      onCancel
    } = options;
    
    // Create overlay and modal (similar to MicPermissionModals pattern)
    const overlay = document.createElement('div');
    overlay.className = 'widget-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;
    
    const modal = document.createElement('div');
    modal.className = 'widget-confirm-modal';
    modal.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;
    
    const confirmBtnClass = confirmButtonClass === 'danger' ? 'widget-modal-btn--danger' : 'widget-modal-btn--primary';
    modal.innerHTML = `
      <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #1e293b;">${title}</h3>
      <p style="margin: 0 0 24px 0; color: #64748b; font-size: 14px; line-height: 1.5;">${message}</p>
      <div style="display: flex; gap: 12px; justify-content: flex-end;">
        <button class="widget-modal-btn widget-modal-btn--cancel" data-action="cancel" style="
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        ">
          ${cancelText}
        </button>
        <button class="widget-modal-btn ${confirmBtnClass}" data-action="confirm" style="
          padding: 10px 20px;
          border: none;
          background: ${confirmButtonClass === 'danger' ? '#ef4444' : '#7c3aed'};
          color: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        ">
          ${confirmText}
        </button>
      </div>
    `;
    
    overlay.appendChild(modal);
    const container = this.shadowRoot || document.body;
    container.appendChild(overlay);
    
    // Event handlers
    modal.querySelector('[data-action="confirm"]').onclick = () => {
      overlay.remove();
      onConfirm?.();
    };
    
    modal.querySelector('[data-action="cancel"]').onclick = () => {
      overlay.remove();
      onCancel?.();
    };
    
    overlay.onclick = (e) => {
      if (e.target === overlay) {
        overlay.remove();
        onCancel?.();
      }
    };
  }

  async startChat() {
    // No-op with single-shot design; connection happens per message
    this.isActive = true;
    this.textInterface.setActive(true);
  }

  async sendMessage() {
    await this.textInterface.sendMessage();
  }

  updateSendButtonState() {
    this.textInterface.updateSendButtonState();
  }

  /**
   * Show warning message inside widget when back button is clicked during active call
   */
  showBackButtonWarning() {
    if (!this.voiceInterface?.isActive) return;
    
    // Show warning message in the voice interface
    if (this.voiceInterface.desktop?.showBackButtonWarning) {
      this.voiceInterface.desktop.showBackButtonWarning();
    }
  }

  async getSignedUrl() {
    // If signedUrl is provided, use it directly
    if (this.config.signedUrl) {
      return this.config.signedUrl;
    }
    
    // Otherwise, construct URL directly from agentId and appId
    if (!this.config.agentId) {
      throw new Error('agentId is required when signedUrl is not provided');
    }
    
    // Build WebSocket URL directly
    // Default uses backend.talktopc.com for text chat (path may need adjustment)
    const baseUrl = this.config.websocketUrl || 'wss://backend.talktopc.com/ws/conv';
    const params = new URLSearchParams();
    params.append('agentId', this.config.agentId);
    
    if (this.config.appId) {
      params.append('appId', this.config.appId);
    }
    
    // Add demo flag if in development
    if (this.config.demo !== false) {
      params.append('demo', 'true');
    }
    
    const wsUrl = `${baseUrl}?${params.toString()}`;
    
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[TTPAgentSDK] WebSocket URL:', wsUrl);
    }
    
    return wsUrl;
  }

  // Delegated to TextInterface
  addMessage(type, text) {
    this.textInterface.addMessage(type, text);
  }

  beginStreaming() {
    this.textInterface.beginStreaming();
  }

  appendStreamingChunk(chunk) {
    this.textInterface.appendStreamingChunk(chunk);
  }

  finalizeStreaming(fullText) {
    this.textInterface.finalizeStreaming(fullText);
  }

  stopStreamingState() {
    this.textInterface.stopStreamingState();
  }

  showError(message) {
    this.textInterface.showError(message);
  }

  updateStatus(status) {
    // Status update handler (can be overridden by users)
  }

  // Public API methods
  updateConfig(newConfig) {
    
    // Deep merge nested objects
    const mergedConfig = { ...this.config };
    
    // Deep merge panel config if it exists (common config)
    if (newConfig.panel) {
      mergedConfig.panel = { ...this.config.panel, ...newConfig.panel };
    }
    
    // Deep merge voice config if it exists
    if (newConfig.voice) {
      mergedConfig.voice = { ...this.config.voice, ...newConfig.voice };
      // Deep merge micButtonHint if it exists
      if (newConfig.voice.micButtonHint) {
        mergedConfig.voice.micButtonHint = {
          ...this.config.voice?.micButtonHint,
          ...newConfig.voice.micButtonHint
        };
      }
    }
    
    // Deep merge text config if it exists
    if (newConfig.text) {
      mergedConfig.text = { ...this.config.text, ...newConfig.text };
      // Deep merge sendButtonHint if it exists
      if (newConfig.text.sendButtonHint) {
        mergedConfig.text.sendButtonHint = {
          ...this.config.text?.sendButtonHint,
          ...newConfig.text.sendButtonHint
        };
      }
    }
    
    // Update language if provided - must be before mergeWithDefaults
    if (newConfig.language !== undefined) {
      mergedConfig.language = newConfig.language;
    }
    
    // Update direction if language changed to RTL language
    if (newConfig.language !== undefined) {
      const rtlLanguages = ['he', 'ar'];
      if (rtlLanguages.includes(newConfig.language)) {
        mergedConfig.direction = 'rtl';
      } else if (newConfig.direction === undefined) {
        mergedConfig.direction = 'ltr';
      }
    }
    
    // Update translations if provided
    if (newConfig.translations) {
      mergedConfig.translations = newConfig.translations;
      this.translations = newConfig.translations;
    }
    
    // Merge other configs (shallow merge is fine for these)
    if (newConfig.button) {
      mergedConfig.button = { ...this.config.button, ...newConfig.button };
    }
    if (newConfig.header) {
      mergedConfig.header = { ...this.config.header, ...newConfig.header };
    }
    if (newConfig.icon) {
      mergedConfig.icon = { ...this.config.icon, ...newConfig.icon };
    }
    if (newConfig.messages) {
      mergedConfig.messages = { ...this.config.messages, ...newConfig.messages };
    }
    if (newConfig.animation) {
      mergedConfig.animation = { ...this.config.animation, ...newConfig.animation };
    }
    if (newConfig.behavior) {
      mergedConfig.behavior = { ...this.config.behavior, ...newConfig.behavior };
    }
    if (newConfig.accessibility) {
      mergedConfig.accessibility = { ...this.config.accessibility, ...newConfig.accessibility };
    }
    if (newConfig.tooltips) {
      mergedConfig.tooltips = { ...this.config.tooltips, ...newConfig.tooltips };
    }
    if (newConfig.landing) {
      mergedConfig.landing = { ...this.config.landing, ...newConfig.landing };
    }
    if (newConfig.footer) {
      mergedConfig.footer = { ...this.config.footer, ...newConfig.footer };
    }
    
    // Merge direction property (only if not set by language change above)
    if (newConfig.direction !== undefined && mergedConfig.direction === undefined) {
      mergedConfig.direction = newConfig.direction;
    }
    
    // Merge primaryColor if provided
    if (newConfig.primaryColor !== undefined) {
      mergedConfig.primaryColor = newConfig.primaryColor;
    }
    
    // Merge event callbacks - preserve existing hooks if not provided in newConfig
    mergedConfig.onConversationStart = newConfig.onConversationStart !== undefined 
      ? newConfig.onConversationStart 
      : this.config.onConversationStart;
    mergedConfig.onConversationEnd = newConfig.onConversationEnd !== undefined 
      ? newConfig.onConversationEnd 
      : this.config.onConversationEnd;
    mergedConfig.onBargeIn = newConfig.onBargeIn !== undefined 
      ? newConfig.onBargeIn 
      : this.config.onBargeIn;
    mergedConfig.onAudioStartPlaying = newConfig.onAudioStartPlaying !== undefined 
      ? newConfig.onAudioStartPlaying 
      : this.config.onAudioStartPlaying;
    mergedConfig.onAudioStoppedPlaying = newConfig.onAudioStoppedPlaying !== undefined 
      ? newConfig.onAudioStoppedPlaying 
      : this.config.onAudioStoppedPlaying;
    mergedConfig.onSubtitleDisplay = newConfig.onSubtitleDisplay !== undefined 
      ? newConfig.onSubtitleDisplay 
      : this.config.onSubtitleDisplay;
    mergedConfig.onVoiceCallButtonClick = newConfig.onVoiceCallButtonClick !== undefined
      ? newConfig.onVoiceCallButtonClick
      : this.config.onVoiceCallButtonClick;

    // Merge useShadowDOM if provided
    if (newConfig.useShadowDOM !== undefined) {
      mergedConfig.useShadowDOM = newConfig.useShadowDOM;
    }
    
    // Merge any other top-level properties
    Object.keys(newConfig).forEach(key => {
      if (!['panel', 'button', 'header', 'footer', 'icon', 'messages', 'direction', 'voice', 'text', 'animation', 'behavior', 'accessibility', 'language', 'tooltips', 'landing', 'primaryColor', 'useShadowDOM', 'onConversationStart', 'onConversationEnd', 'onBargeIn', 'onAudioStartPlaying', 'onAudioStoppedPlaying', 'onSubtitleDisplay', 'onVoiceCallButtonClick'].includes(key)) {
        mergedConfig[key] = newConfig[key];
      }
    });
    
    // Store current language before merge to detect changes
    const oldLanguage = this.config?.language || 'en';
    // Store current useShadowDOM setting
    const oldUseShadowDOM = this.config?.useShadowDOM !== false;
    
    this.config = this.mergeWithDefaults(mergedConfig);
    
    // Check if useShadowDOM changed - if so, need to recreate container
    const useShadowDOMChanged = (this.config.useShadowDOM !== false) !== oldUseShadowDOM;
    
    if (useShadowDOMChanged || !this.container) {
      // Remove existing containers
      const existingHost = document.getElementById('ttp-widget-shadow-host');
      if (existingHost) {
        existingHost.remove();
      }
      const existingContainer = document.getElementById('ttp-widget-container');
      if (existingContainer) {
        existingContainer.remove();
      }
      
      // Remove styles from head if switching away from non-Shadow DOM
      if (oldUseShadowDOM === false) {
        const existingStyle = document.getElementById('ttp-widget-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      }
      
      // Recreate container based on new setting
      if (this.config.useShadowDOM) {
        this.createShadowHost();
        this.container = this.shadowRoot;
      } else {
        this.createRegularContainer();
        this.container = this.regularContainer;
        this.shadowRoot = document;
      }
    } else {
      // Just clean up existing containers if they exist
      const existingHost = document.getElementById('ttp-widget-shadow-host');
      if (existingHost && !this.config.useShadowDOM) {
        existingHost.remove();
      }
      const existingContainer = document.getElementById('ttp-widget-container');
      if (existingContainer && this.config.useShadowDOM) {
        existingContainer.remove();
      }
      
      // Ensure container references are correct
      if (this.config.useShadowDOM) {
        if (!this.shadowRoot) {
          const host = document.getElementById('ttp-widget-shadow-host');
          if (host) {
            this.shadowRoot = host.shadowRoot;
            this.container = this.shadowRoot;
          } else {
            this.createShadowHost();
            this.container = this.shadowRoot;
          }
        } else {
          this.container = this.shadowRoot;
        }
      } else {
        if (!this.regularContainer) {
          const container = document.getElementById('ttp-widget-container');
          if (container) {
            this.regularContainer = container;
            this.container = container;
          } else {
            this.createRegularContainer();
            this.container = this.regularContainer;
          }
        } else {
          this.container = this.regularContainer;
        }
        this.shadowRoot = document;
      }
    }
    
    // Recreate interfaces with new config (after mergeWithDefaults, so language is current)
    // Ensure language is correctly passed - use the merged config value
    const currentLanguage = this.config.language || 'en';
    const voiceConfig = {
      ...this.config,
      ...this.config.voice,
      language: currentLanguage, // Use the current language from merged config
      translations: this.translations,
      shadowRoot: this.shadowRoot, // Pass shadow root reference
      signedUrl: this.config.signedUrl, // Pass through signedUrl if provided
      agentSettingsOverride: this.config.agentSettingsOverride, // Pass through agentSettingsOverride if provided
      visualAssistant: this.config.visualAssistant, // Pass through visualAssistant config
      // Output format defaults for widget: 44100 Hz, 16-bit, PCM, raw
      outputContainer: this.config.voice?.outputContainer || 'raw',
      outputEncoding: this.config.voice?.outputEncoding || 'pcm',
      outputSampleRate: this.config.voice?.outputSampleRate || 44100,
      outputChannels: this.config.voice?.outputChannels || 1,
      outputBitDepth: this.config.voice?.outputBitDepth || 16,
      // Pass event callbacks to voice interface
      onConversationStart: this.config.onConversationStart,
      onConversationEnd: this.config.onConversationEnd,
      onBargeIn: this.config.onBargeIn,
      onAudioStartPlaying: this.config.onAudioStartPlaying,
      onAudioStoppedPlaying: this.config.onAudioStoppedPlaying,
      onSubtitleDisplay: this.config.onSubtitleDisplay
    };
    this.voiceInterface = new VoiceInterface(voiceConfig);
    const textConfig = {
      ...this.config,
      ...this.config.text,
      language: currentLanguage, // Ensure language is set from merged config
      translations: this.translations,
      shadowRoot: this.shadowRoot // Pass shadow root reference
    };
    this.textInterface = new TextInterface(textConfig, this.sdk);
    
    // Recreate landing screen if in unified mode
    const widgetMode = this.config.behavior?.mode || 'unified';
    if (widgetMode === 'unified') {
      const landingConfig = {
        ...this.config,
        translations: this.translations,
        shadowRoot: this.shadowRoot
      };
      this.landingScreen = new LandingScreen(landingConfig);
    } else {
      this.landingScreen = null;
    }
    
    // Recreate widget with new config (after interfaces are created)
    this.createWidget();
    
    // Update input attributes in case widget was recreated
    if (this.textInterface && this.textInterface.updateInputAttributes) {
      this.textInterface.updateInputAttributes();
    }
  }

  destroy() {
    // Clear prompt animation timer if it exists
    if (this.promptAutoHideTimer) {
      clearTimeout(this.promptAutoHideTimer);
      this.promptAutoHideTimer = null;
    }
    
    // Remove shadow host if it exists
    const host = document.getElementById('ttp-widget-shadow-host');
    if (host) {
      host.remove();
    }
    // Remove regular container if it exists
    const container = document.getElementById('ttp-widget-container');
    if (container) {
      container.remove();
    }
    // Remove styles from head if they exist
    const style = document.getElementById('ttp-widget-styles');
    if (style) {
      style.remove();
    }
    if (this.sdk) {
      this.sdk.destroy();
    }
    // Clean up interface resources
    if (this.voiceInterface) {
      this.voiceInterface.destroy();
    }
  }

  // Delegated to VoiceInterface
  async startVoiceCall() {
    // Note: isStartingCall is managed by the caller (voice card handler)
    // This method just delegates to voiceInterface
    await this.voiceInterface.startVoiceCall();
    // Flush pending tools after SDK connects
    this._flushPendingClientTools();
  }

  endVoiceCall() {
    this.voiceInterface.endVoiceCall();
  }

  toggleMute() {
    this.voiceInterface.toggleMute();
  }

  toggleSpeaker() {
    this.voiceInterface.toggleSpeaker();
  }

  /**
   * Inject data/text into the active voice conversation programmatically.
   * This method delegates to the underlying VoiceSDK and returns a Promise.
   * 
   * @param {string} data - The data/text to inject
   * @param {Object} options - Optional configuration
   * @param {string} options.source - Source identifier (default: 'widget_programmatic')
   * @param {Object} options.metadata - Optional metadata (will be echoed back in ack)
   * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
   * @returns {Promise<Object>} Promise resolving to acknowledgment message on success
   * 
   * @example
   * const widget = new TTPChatWidget({ ... });
   * 
   * // Simple usage
   * try {
   *   const ack = await widget.injectData('User ID: 12345');
   *   console.log('Data injected:', ack);
   * } catch (error) {
   *   console.error('Failed:', error);
   * }
   * 
   * // With metadata
   * const ack = await widget.injectData('Form submitted', {
   *   metadata: { formId: 'contact' }
   * });
   */
  async injectData(data, options = {}) {
    // Validation: data content
    if (!data || !data.trim()) {
      console.warn('TTPChatWidget: Cannot inject empty data');
      throw new Error('Cannot inject empty data');
    }

    // Check if voice interface is active
    if (!this.voiceInterface || !this.voiceInterface.isActive) {
      console.warn('TTPChatWidget: Cannot inject data - voice call is not active');
      throw new Error('Voice call is not active');
    }

    // Check if SDK is available
    const voiceSDK = this.voiceInterface?.getVoiceSDK();
    if (!voiceSDK) {
      console.warn('TTPChatWidget: VoiceSDK not available');
      throw new Error('VoiceSDK not available');
    }

    // Delegate to SDK method (which handles correlation IDs and Promise resolution)
    // Widget just passes through options with default source
    try {
      const ack = await voiceSDK.injectData(data, {
        ...options,
        source: options.source || 'widget_programmatic'
      });
      return ack;
    } catch (error) {
      // Re-throw error (SDK already logged it)
      throw error;
    }
  }

  /**
   * Register a handler for a client tool
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @example
   * widget.registerToolHandler('getPageContent', async (params) => {
   *   return { title: document.title, url: window.location.href };
   * });
   */
  registerToolHandler(name, handler) {
    if (this.voiceInterface?.sdk) {
      return this.voiceInterface.sdk.registerToolHandler(name, handler);
    }
    // Queue for later if SDK not ready
    this._pendingClientTools = this._pendingClientTools || [];
    this._pendingClientTools.push({ name, handler });
    console.warn('TTPChatWidget: Tool handler queued - voice interface SDK not ready yet');
    // Try to flush immediately in case SDK becomes ready
    this._flushPendingClientTools();
    return this;
  }

  /**
   * Unregister a tool handler
   * @param {string} name - Tool name
   */
  unregisterToolHandler(name) {
    if (this.voiceInterface?.sdk) {
      return this.voiceInterface.sdk.unregisterToolHandler(name);
    }
    // Remove from pending queue if exists
    if (this._pendingClientTools) {
      this._pendingClientTools = this._pendingClientTools.filter(tool => tool.name !== name);
    }
    return this;
  }

  /**
   * Check if a tool handler is registered
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasToolHandler(name) {
    if (this.voiceInterface?.sdk) {
      return this.voiceInterface.sdk.hasToolHandler(name);
    }
    return false;
  }

  /**
   * Get all registered tool handler names
   * @returns {string[]}
   */
  getRegisteredTools() {
    if (this.voiceInterface?.sdk) {
      return this.voiceInterface.sdk.getRegisteredTools();
    }
    // Return pending tool names if SDK not ready
    if (this._pendingClientTools) {
      return this._pendingClientTools.map(tool => tool.name);
    }
    return [];
  }

  /**
   * Flush pending client tools when SDK is ready
   * @private
   */
  _flushPendingClientTools() {
    if (this._pendingClientTools && this.voiceInterface?.sdk) {
      this._pendingClientTools.forEach(({ name, handler }) => {
        this.voiceInterface.sdk.registerToolHandler(name, handler);
      });
      this._pendingClientTools = null;
      console.log('TTPChatWidget: Flushed pending client tools');
    }
  }

  /**
   * Register a client tool handler (alias for registerToolHandler)
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @returns {this} For method chaining
   * @example
   * widget.registerClientTool('getLocation', async (params) => {
   *   const pos = await new Promise((resolve, reject) => {
   *     navigator.geolocation.getCurrentPosition(resolve, reject, {
   *       enableHighAccuracy: params.highAccuracy
   *     });
   *   });
   *   return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
   * });
   */
  registerClientTool(name, handler) {
    return this.registerToolHandler(name, handler);
  }

  /**
   * Unregister a client tool handler (alias for unregisterToolHandler)
   * @param {string} name - Tool name to unregister
   * @returns {this}
   */
  unregisterClientTool(name) {
    return this.unregisterToolHandler(name);
  }

  /**
   * Check if a client tool is registered (alias for hasToolHandler)
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasClientTool(name) {
    return this.hasToolHandler(name);
  }

  /**
   * Get list of registered client tool names (alias for getRegisteredTools)
   * @returns {string[]}
   */
  getRegisteredClientTools() {
    return this.getRegisteredTools();
  }
}

